// services/AuthService.ts
import { supabase } from "@/services/supabase/client";
import type { Session, User } from "@supabase/supabase-js";

// Define the request/response types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResult {
  user: User;
  session: Session;
}

interface ForgotPasswordRequest {
  email: string;
}

export interface ChangePasswordRequest {
  email: string;
  currentPassword: string;
  newPassword: string;
}

const login = async ({
  email,
  password,
}: LoginRequest): Promise<LoginResult> => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    throw new Error(error.message || "Login failed");
  }

  return { user: data.user, session: data.session };
};


const forgotPassword = async ({
  email,
}: ForgotPasswordRequest): Promise<{ message: string }> => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/create-password`,
  });

  if (error) {
    throw new Error(error.message || "Failed to send reset email");
  }

  return { message: "Password reset email sent!" };
};

const changePassword = async ({
  email,
  currentPassword,
  newPassword,
}: ChangePasswordRequest): Promise<void> => {
  // Supabase has no "verify current password" call on its own — the
  // standard way to confirm it is to re-authenticate with it before
  // updating, so a stolen/left-open session can't silently change the
  // password without knowing the current one.
  const { error: verifyError } = await supabase.auth.signInWithPassword({
    email,
    password: currentPassword,
  });

  if (verifyError) {
    throw new Error("Current password is incorrect");
  }

  const { error: updateError } = await supabase.auth.updateUser({
    password: newPassword,
  });

  if (updateError) {
    throw new Error(updateError.message || "Failed to update password");
  }
};

const AuthService = {
  login,
  forgotPassword,
  changePassword,
};

export default AuthService;
